import { ApolloLink, HttpLink, useApolloClient } from "@apollo/client"
import { PluginState } from "client/graphql"
import {
  GetShopByChannelIdDocument,
  ShopFieldsFragment,
  useActiveSaleUpdatedSubscription,
  useGetShopByChannelIdQuery,
  useSaleActivatedOrDeactivatedSubscription,
  useShopConnectionForChannelUpdatedSubscription,
} from "client/graphql/commerce-api"
import {
  ShopDetailsFieldsFragment,
  useShopDetailsQuery,
} from "client/graphql/storefront-api"
import { useActivePlugins } from "client/packages/utils/useActivePlugins"
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react"

import { useChannelContext } from "../../../components/channel"
import { ProductVariant } from "../utils/getDefaultVariantForProduct"

interface Props {
  children?: ReactNode
}

export enum CommerceView {
  PRODUCT = "PRODUCT",
  CART = "CART",
}

type ShopState = {
  shop: ShopFieldsFragment | null
  shopDetails: ShopDetailsFieldsFragment | null

  currentlySelectedProductId: string | null
  setCurrentlySelectedProductId: Dispatch<SetStateAction<string | null>>

  currentlySelectedVariantId: string | null
  setCurrentlySelectedVariantId: Dispatch<SetStateAction<string | null>>

  view: CommerceView
  setView: Dispatch<SetStateAction<CommerceView>>

  showImageGallery: boolean
  setShowImageGallery: Dispatch<SetStateAction<boolean>>

  addedProduct: AddedToCartItem | null
  setAddedProduct: Dispatch<SetStateAction<AddedToCartItem | null>>

  resetView: () => void

  isStorefrontLinkReady: boolean
}

const ShopContext = createContext<ShopState | undefined>(undefined)

export type AddedToCartItem = {
  variant: ProductVariant
  quantity: number
}

const ShopProvider: FC<Props> = ({ children }) => {
  const { channelId } = useChannelContext()
  const plugins = useActivePlugins()
  const apolloClient = useApolloClient()

  const [currentlySelectedProductId, setCurrentlySelectedProductId] =
    useState<string | null>(null)
  const [currentlySelectedVariantId, setCurrentlySelectedVariantId] =
    useState<string | null>(null)
  const [view, setView] = useState(CommerceView.PRODUCT)
  const [isStorefrontLinkReady, setIsStorefrontLinkReady] = useState(false)
  const [showImageGallery, setShowImageGallery] = useState(false)
  const [addedProduct, setAddedProduct] = useState<AddedToCartItem | null>(
    null,
  )

  // Make initial shop query if commerce is enabled
  const { data: shopData, loading: shopLoading } =
    useGetShopByChannelIdQuery({
      variables: { channelId },
    })

  // Subscribe to updates when sale is activated/deactivated
  useSaleActivatedOrDeactivatedSubscription({
    skip: !shopData?.shopByChannelId,
    variables: { shopId: shopData?.shopByChannelId?.id },
  })

  useShopConnectionForChannelUpdatedSubscription({
    variables: { channelId },
    onData: ({ client, data: { data } }) => {
      const shop = data.shopConnectionForChannelUpdated
      client.writeQuery({
        query: GetShopByChannelIdDocument,
        variables: { channelId },
        data: { shopByChannelId: shop },
      })
      if (!shop) {
        setIsStorefrontLinkReady(false)
      }
    },
  })

  // Subscribe to attribute updates to the active sale
  useActiveSaleUpdatedSubscription({
    skip: !shopData,
    variables: { shopId: shopData?.shopByChannelId?.id },
  })

  const { data: { shop: shopDetails } = {}, loading: shopDetailsLoading } =
    useShopDetailsQuery({
      skip: !isStorefrontLinkReady,
      context: { useStorefrontAPI: true },
    })

  const shop = shopData?.shopByChannelId

  useEffect(() => {
    if (shop) {
      // NOTE:
      // An alternative approach would be to create a second apollo client which
      // is used specifically for the Storefront API. It might be more self-contained
      // than having the plugin alter the top-level apollo client.
      const storefrontLink = new HttpLink({
        uri: `https://${shop.shopDomain}/api/2024-01/graphql.json`,
        headers: {
          "X-Shopify-Storefront-Access-Token": shop.storefrontAccessToken,
        },
      })

      const splitLink = new ApolloLink((operation, forward) =>
        forward(operation),
      ).split(
        ({ getContext }) => {
          return Boolean(getContext().useStorefrontAPI)
        },
        storefrontLink,
        apolloClient.link,
      )

      apolloClient.setLink(splitLink)
      setIsStorefrontLinkReady(true)
    }
  }, [shop])

  // If commerce is disabled, delete local shop data from the cache.
  // Since we don't receive shop updates when a broadcast is stopped, we have to do this ourselves.
  useEffect(() => {
    if (plugins?.commerce?.state !== PluginState.Enabled) {
      apolloClient.cache.evict({
        id: "ROOT_QUERY",
        fieldName: "shopByChannelId",
      })
      apolloClient.cache.evict({
        id: "ROOT_SUBSCRIPTION",
        fieldName: "activeSaleUpdated",
      })
      apolloClient.cache.gc()
    }
  }, [plugins?.commerce?.state])

  const resetView = () => {
    setView(CommerceView.PRODUCT)
    setCurrentlySelectedProductId(null)
    setCurrentlySelectedVariantId(null)
    setAddedProduct(null)
  }

  // Reset view state when sale id changes
  useEffect(() => {
    resetView()
  }, [shop?.activeSale?.id])

  if (shopDetailsLoading || shopLoading) {
    return null
  }

  const shopState: ShopState = {
    shop,
    shopDetails,
    currentlySelectedProductId,
    setCurrentlySelectedProductId,
    currentlySelectedVariantId,
    setCurrentlySelectedVariantId,
    view,
    setView,
    showImageGallery,
    setShowImageGallery,
    addedProduct,
    setAddedProduct,
    resetView,
    isStorefrontLinkReady,
  }

  return (
    <ShopContext.Provider value={shopState}>
      {children}
    </ShopContext.Provider>
  )
}

const useShopContext = () => {
  const shopContext = useContext(ShopContext)
  if (!shopContext) {
    throw new Error(
      "No ShopContext.Provider found when calling useShopContext.",
    )
  }
  return shopContext
}

export { ShopProvider, useShopContext }
